<template>
  <div class="fixed right-6 bottom-4 z-50 flex flex-col items-end">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="is_open"
        :class="is_fullscreen ? 'fixed inset-0 z-50' : 'relative mb-1 -translate-x-8'"
      >
        <div
          :class="
            is_fullscreen
              ? 'flex h-full w-full flex-col overflow-hidden bg-surface-0'
              : 'flex h-[680px] max-h-[calc(100svh-8rem)] w-[520px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border border-surface-200 bg-surface-0 shadow-2xl'
          "
        >
          <AiChatHeader
            :context_label="context_label"
            :show_history="show_history"
            :is_fullscreen="is_fullscreen"
            @clear="clear_conversation"
            @toggle_history="toggle_history"
            @toggle_fullscreen="toggle_fullscreen"
          />

          <Transition
            mode="out-in"
            enter-active-class="transition duration-150"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <AiChatHistory
              v-if="show_history"
              key="history"
              :chats="past_chats"
              :is_loading="history_loading"
              @select="load_chat"
            />
            <div
              v-else
              key="chat"
              class="flex flex-1 flex-col overflow-hidden"
            >
              <AiChatMessages
                :messages="messages"
                :is_loading="is_loading"
                :suggestions="suggestions"
                :cap_reached="cap_reached"
                :ziggy_laptop="ziggy_laptop"
                @send_suggestion="submit"
                @new_conversation="clear_conversation"
                @close_assistant="close"
              />
              <AiChatInput
                :key="conversation_key"
                :is_loading="is_loading"
                :disabled="cap_reached"
                @submit="submit"
              />
            </div>
          </Transition>
        </div>

        <!-- Speech bubble tail -->
        <template v-if="!is_fullscreen">
          <svg
            class="absolute right-8 -bottom-[46px]"
            width="56"
            height="48"
            viewBox="0 0 56 48"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M -2,0 C 0,30 44,43 53,47 C 35,28 22,8 34,0 Z"
              fill="white"
            />
            <path
              d="M 2,0 C 2,30 44,43 53,47 C 35,28 22,8 34,0"
              fill="none"
              stroke="#e2e8f0"
              stroke-width="1"
              stroke-linejoin="round"
            />
          </svg>
        </template>
      </div>
    </Transition>

    <button
      class="relative mt-3 block transition-all duration-200 hover:scale-105"
      @click="toggle"
    >
      <img
        :src="ziggy_laptop"
        alt="Ziggy"
        class="h-24 w-24 object-contain drop-shadow-lg"
      />
      <div
        v-if="is_open"
        class="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-surface-700 text-white shadow"
      >
        <AppIcon
          name="solid-xmark"
          class="text-xs"
        />
      </div>
    </button>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import ziggy_laptop from '@/assets/images/ziggy/Ziggy_laptop.png';
import AiChatHeader from './AiChatHeader.vue';
import AiChatMessages from './AiChatMessages.vue';
import AiChatInput from './AiChatInput.vue';
import AiChatHistory from './AiChatHistory.vue';

const { t } = useI18n();
const { current_project } = mapGetters(['current_project']);
const { FETCH_AI_CHATS, CREATE_AI_CHAT, CREATE_AI_MESSAGE, FETCH_AI_MESSAGES } = mapActions([
  'FETCH_AI_CHATS',
  'CREATE_AI_CHAT',
  'FETCH_AI_MESSAGES',
  'CREATE_AI_MESSAGE',
]);

const is_open = ref(false);
const is_fullscreen = ref(false);
const is_loading = ref(false);
const messages = ref([]);
const show_history = ref(false);
const past_chats = ref([]);
const history_loading = ref(false);
const conversation_key = ref(0);
const current_session_id = ref(null);
const cap_reached = ref(false);

const context_label = computed(() =>
  current_project.value ? current_project.value.attributes.name : t('ai.assistant.all_projects'),
);

const suggestions = computed(() => [
  t('ai.assistant.suggestions.open_decisions'),
  t('ai.assistant.suggestions.unassigned_units'),
  t('ai.assistant.suggestions.help_notifications'),
  t('ai.assistant.suggestions.help_project_settings'),
]);

function toggle() {
  is_open.value = !is_open.value;
  if (!is_open.value) is_fullscreen.value = false;
}

function close() {
  is_open.value = false;
  is_fullscreen.value = false;
}

function toggle_fullscreen() {
  is_fullscreen.value = !is_fullscreen.value;
}

async function submit(question) {
  if (!question || is_loading.value || cap_reached.value) return;

  messages.value.push({ attributes: { role: 'user', content: question } });
  is_loading.value = true;

  try {
    if (!current_session_id.value) {
      const session = await CREATE_AI_CHAT({ record: {} });
      current_session_id.value = session.id;
    }

    const message = await CREATE_AI_MESSAGE({
      record: {
        attributes: {
          chat_id: current_session_id.value,
          question,
        },
      },
    });
    messages.value.push(message);
    if (message.attributes.cap_reached) cap_reached.value = true;
  } catch (error) {
    const code = _.get(error, 'response.data.errors[0].code');
    const content =
      code === 'ai_budget_reached' ? t('ai.assistant.errors.budget_reached') : t('ai.assistant.errors.generic');
    messages.value.push({ attributes: { role: 'assistant', content } });
  } finally {
    is_loading.value = false;
  }
}

function clear_conversation() {
  messages.value = [];
  show_history.value = false;
  current_session_id.value = null;
  cap_reached.value = false;
  conversation_key.value += 1;
}

async function toggle_history() {
  show_history.value = !show_history.value;
  if (show_history.value) {
    await fetch_history();
  }
}

async function fetch_history() {
  history_loading.value = true;
  try {
    past_chats.value = await FETCH_AI_CHATS();
  } finally {
    history_loading.value = false;
  }
}

async function load_chat(chat) {
  messages.value = await FETCH_AI_MESSAGES({ chat_id: chat.id });
  current_session_id.value = chat.id;
  show_history.value = false;
  conversation_key.value += 1;
}
</script>
